import React, { useState, useEffect } from 'react';
import { Button, Modal, Form, Input, Select, message, Card } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import DataTable from '../components/DataTable';
import { ServiceData, ResourceUnit, TaxValue, RESOURCE_UNIT_LABEL_KEYS } from '../models/Resource';
import {
  getAllServices,
  createService,
  updateService,
  deleteService
} from '../services/supplierService';
import { getApiErrorMessage } from '../utils/errorMessages';
import { useTranslation } from 'react-i18next';
const { Option } = Select;

const Services: React.FC = () => {
  const [data, setData] = useState<ServiceData[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<ServiceData | null>(null);
  const [form] = Form.useForm();
  const { t } = useTranslation();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getAllServices();
      const serviceArray = response.products || response;
      const formattedData = serviceArray.map((item: any) => {
        const variant = item.variants?.[0];
        const price = variant?.prices?.[0];
        return {
          id: item.id,
          id_variant: variant?.id,
          id_price: price?.id,
          name: item.title,
          description: item.description || '',
          unit: variant?.metadata?.unit || '',
          unit_price: price?.amount?.toString() || '',
          tax_value: variant?.metadata?.tax_value || '',
          handle: item.handle,
          category: item.categories?.[0]?.id || '',
          sub_category: item.categories?.[1]?.id || ''
        };
      });
      setData(formattedData);
    } catch (error: any) {
      message.error(t('loadError'));
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<ServiceData> = [
    {
      title: t('name'),
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: t('description'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: t('unit'),
      dataIndex: 'unit',
      key: 'unit',
      render: (unitValue: string) =>
        unitValue ? t(RESOURCE_UNIT_LABEL_KEYS[unitValue] || unitValue) : '-',
    },
    {
      title: t('unitPrice'),
      dataIndex: 'unit_price',
      key: 'unit_price',
      render: (price: string) => (price ? `${Number(price).toFixed(2)} €` : '-'),
    },
    {
      title: t('taxValue'),
      dataIndex: 'tax_value',
      key: 'tax_value',
      render: (tax: string) => `${tax}%`,
    },
  ];

  const handleCreate = () => {
    setEditingItem(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: ServiceData) => {
    setEditingItem(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteService(id);
      message.success(t('deleteSuccess'));
      fetchData();
    } catch (error: any) {
      message.error(getApiErrorMessage(error, 'deleteError', t));
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingItem) {
        await updateService(editingItem.id!, {
          ...values,
          id_variant: editingItem.id_variant,
          id_price: editingItem.id_price
        });
        message.success(t('updateSuccess'));
      } else {
        await createService(values);
        message.success(t('createSuccess'));
      }
      setModalVisible(false);
      form.resetFields();
      fetchData();
    } catch (error: any) {
      message.error(getApiErrorMessage(error, 'saveError', t));
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ fontSize: 24, fontWeight: 600, color: '#334E3B', margin: 0, marginBottom: 16 }}>
        {t('servicesManagement')}
      </h1>
      <Card
          extra={
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              {t('add')}
            </Button>
          }
        >
          <DataTable
            data={data}
            columns={columns}
            loading={loading}
            onEdit={handleEdit}
            onDelete={handleDelete}
            searchPlaceholder={t('search')}
          />
        </Card>

        <Modal
          title={editingItem ? t('editService') : t('addService')}
          open={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
          }}
          onOk={() => form.submit()}
          width={700}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Form.Item
              name="name"
              label={t('name')}
              rules={[{ required: true, message: t('nameRequired') }]}
            >
              <Input placeholder={t('name')} />
            </Form.Item>

            <Form.Item
              name="description"
              label={t('description')}
              rules={[{ required: true, message: t('descriptionRequired') }]}
            >
              <Input.TextArea rows={3} placeholder={t('description')} />
            </Form.Item>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <Form.Item
                name="unit"
                label={t('unit')}
                rules={[{ required: true, message: t('unitRequired') }]}
              >
                <Select placeholder={t('selectUnit')}>
                  {Object.values(ResourceUnit).map((value) => (
                    <Option key={value} value={value}>
                      {t(RESOURCE_UNIT_LABEL_KEYS[value])}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="tax_value"
                label={t('taxValue')}
                rules={[{ required: true, message: t('taxValueRequired') }]}
              >
                <Select placeholder={t('selectTaxValue')}>
                  {Object.values(TaxValue).map((value) => (
                    <Option key={value} value={value}>
                      {value}%
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            <Form.Item
              name="unit_price"
              label={t('unitPrice')}
              rules={[
                { required: true, message: t('unitPriceRequired') },
                { pattern: /^\d+([.,]\d{1,2})?$/, message: t('invalidFormat') }
              ]}
            >
              <Input placeholder={t('unitPrice')} />
            </Form.Item>
          </Form>
        </Modal>
    </div>
  );
};

export default Services;
